import { omit } from "lodash";
import { DateTime } from "luxon";
import React, { FC } from "react";

import { Card, CardContent, Skeleton } from "@mui/material";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import {
  AllSeriesType,
  AxisConfig,
  ChartContainer,
  ChartsGrid,
  ChartsTooltip,
  ChartsXAxis,
  ChartsXAxisProps,
  ChartsYAxis,
  LinePlot,
} from "@mui/x-charts";

import useTimePeriod from "../../../hooks/use-time-period";
import { useSelector } from "../../../store";
import {
  GetDistributionTimeSeriesApiArg,
  useGetDistributionTimeSeriesQuery,
} from "../../../store/pomeriumApi";
import { formatDurationMS } from "../../../utils/format";

const RequestDuration: FC = () => {
  const { period } = useTimePeriod();
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const { routeId } = useSelector((state) => state.metrics);
  const now = DateTime.now().startOf("minute");
  const start = now.minus(period);
  let params: GetDistributionTimeSeriesApiArg = {
    organizationId,
    clusterId,
    startTime: start.toISO(),
    endTime: now.toISO(),
    routeId: routeId,
    metricId: "upstream_request_time",
  };
  if (!routeId) {
    params = omit(params, ["routeId"]);
  }

  const { data: reqDurations } = useGetDistributionTimeSeriesQuery(params, {
    skip: !organizationId || !clusterId,
  });

  const transformed = reqDurations?.map((ts) => ({
    series: {
      id: ts.labels.percentile + "requestDuration",
      label: ts.labels.percentile.substring(1, 3) + "th Percentile",
      type: "line",
      data: ts.points.map((p) => p.value),
      valueFormatter: (value: number) => formatDurationMS(value),
    } as AllSeriesType<"line">,
    xAxis: {
      id: "requestDurationX",
      data: ts.points.map((p) => new Date(p.timestamp)),
      scaleType: "utc",
      min: start.toJSDate(),
      max: now.toJSDate(),
    } as AxisConfig<"utc", any, ChartsXAxisProps>,
  }));

  return (
    <Card
      sx={{
        width: "100%",
        m: 2,
      }}
    >
      <CardContent sx={{ height: "40vh", m: 2 }}>
        <Typography variant={"h4"} color="text.primary" gutterBottom>
          Request Duration
        </Typography>
        {!reqDurations && (
          <Skeleton width="100%" height="100%" sx={{ transform: "unset" }} />
        )}
        {reqDurations && !reqDurations?.length && (
          <Alert severity="info">
            No data to display. Data is updated hourly.
          </Alert>
        )}
        {transformed && !!reqDurations?.length && (
          <ChartContainer
            sx={{ padding: "10px" }}
            xAxis={transformed.map((d) => d.xAxis)}
            series={transformed.map((d) => d.series)}
            yAxis={[
              {
                valueFormatter: (value: number) => formatDurationMS(value),
              },
            ]}
          >
            <LinePlot />
            <ChartsXAxis axisId={"requestDurationX"} />
            <ChartsYAxis />
            <ChartsGrid horizontal />
            <ChartsTooltip trigger="axis" />
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default RequestDuration;
